import type { NodeType, ProjectNode } from '@/shared/types/projectData';
import {
  hasDocumentRecordMarkers,
  isMeetingRecord,
} from '@/shared/utils/documentRecordClassification';

export const NODE_TYPE_CONVERSION_DESCENDANTS_ERROR = 'Only groups can contain children.';
export const NODE_TYPE_CONVERSION_MILESTONE_DATE_ERROR = 'Cannot convert to milestone without an existing usable date.';
export const NODE_TYPE_CONVERSION_INVALID_TARGET_ERROR = 'Invalid node type conversion target.';
export const NODE_TYPE_DOCUMENT_NORMALIZATION_MARKER_ERROR = 'Only existing document-marked legacy records can be normalized to Note.';
export const NODE_TYPE_DOCUMENT_NORMALIZATION_DESCENDANTS_ERROR = 'Cannot normalize a note record while it contains child nodes.';

const CONVERTIBLE_NODE_TYPES: NodeType[] = ['group', 'task', 'milestone'];

const canonicalNodeId = (nodeId?: string | null): string => {
  if (!nodeId) return '';
  return nodeId.includes(':') ? nodeId.split(':').slice(1).join(':') : nodeId;
};

const matchesNodeId = (left?: string | null, right?: string | null): boolean => {
  if (!left || !right) return false;
  if (left.includes(':') && right.includes(':')) return left === right;
  return left === right || canonicalNodeId(left) === canonicalNodeId(right);
};

export const scopedNodeId = (node: Partial<ProjectNode>, id?: string | null): string | undefined => {
  if (!id) return undefined;
  if (id.includes(':')) return id;
  const owner = node.id?.includes(':') ? node.id.split(':')[0] : node.projectId;
  return owner && owner !== 'default' ? `${owner}:${id}` : id;
};

export const findProjectNodeById = (
  nodes: Array<Partial<ProjectNode>>,
  nodeId?: string | null,
): Partial<ProjectNode> | undefined => {
  if (!nodeId) return undefined;
  const exactNode = nodes.find((node) => node.id === nodeId);
  if (exactNode) return exactNode;
  const candidates = nodes.filter((node) => matchesNodeId(scopedNodeId(node, node.id), nodeId));
  return candidates.length === 1 ? candidates[0] : undefined;
};

export const parentCanAcceptChildren = (
  nodes: Array<Partial<ProjectNode>>,
  parentId?: string | null,
): boolean => {
  if (!parentId) {
    return true;
  }

  const parentNode = findProjectNodeById(nodes, parentId);
  if (!parentNode) {
    return false;
  }

  return parentNode.type === 'group';
};

export const getDescendantNodeIds = (
  nodes: Array<Partial<ProjectNode>>,
  nodeId?: string | null,
): string[] => {
  if (!nodeId) return [];

  const descendants: string[] = [];
  const pendingParentIds = new Set<string>([nodeId]);
  const visitedNodeIds = new Set<string>();

  while (pendingParentIds.size > 0) {
    const currentParentIds = Array.from(pendingParentIds);
    pendingParentIds.clear();

    nodes.forEach((node) => {
      if (!node.id || visitedNodeIds.has(node.id) || !node.parentId) {
        return;
      }

      const isChild = currentParentIds.some((parentId) => matchesNodeId(scopedNodeId(node, node.parentId), parentId));
      if (!isChild) {
        return;
      }

      descendants.push(node.id);
      visitedNodeIds.add(node.id);
      pendingParentIds.add(node.id);
    });
  }

  return descendants;
};

export const resolveUsableMilestoneDate = (
  node: Partial<ProjectNode>,
  pendingUpdates?: Partial<ProjectNode>,
): string | null => {
  const candidates = [
    pendingUpdates?.date,
    pendingUpdates?.dueDate,
    pendingUpdates?.endDate,
    pendingUpdates?.startDate,
    node.date,
    node.dueDate,
    node.endDate,
    node.startDate,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }

  return null;
};

const mergeTags = (...tagLists: Array<unknown>): string[] => {
  const tags = new Set<string>();
  tagLists.forEach((tagList) => {
    if (!Array.isArray(tagList)) {
      return;
    }
    tagList.forEach((tag) => {
      const normalizedTag = String(tag ?? '').trim();
      if (normalizedTag) {
        tags.add(normalizedTag);
      }
    });
  });
  tags.add('document');
  return Array.from(tags);
};

export interface NodeTypeConversionPolicyResult {
  allowed: boolean;
  reason?: string;
  targetType: NodeType;
  descendantCount: number;
  usableMilestoneDate: string | null;
}

export const getNodeTypeConversionPolicy = (
  node: Partial<ProjectNode>,
  targetType: NodeType,
  nodes: Array<Partial<ProjectNode>>,
  pendingUpdates?: Partial<ProjectNode>,
): NodeTypeConversionPolicyResult => {
  if (node.type === 'document' || !CONVERTIBLE_NODE_TYPES.includes(targetType)) {
    return {
      allowed: false,
      reason: NODE_TYPE_CONVERSION_INVALID_TARGET_ERROR,
      targetType,
      descendantCount: 0,
      usableMilestoneDate: null,
    };
  }

  const descendantIds = getDescendantNodeIds(nodes, node.id);
  const usableMilestoneDate = resolveUsableMilestoneDate(node, pendingUpdates);

  if (targetType !== 'group' && descendantIds.length > 0) {
    return {
      allowed: false,
      reason: NODE_TYPE_CONVERSION_DESCENDANTS_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate,
    };
  }

  if (targetType === 'milestone' && !usableMilestoneDate) {
    return {
      allowed: false,
      reason: NODE_TYPE_CONVERSION_MILESTONE_DATE_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  return {
    allowed: true,
    targetType,
    descendantCount: descendantIds.length,
    usableMilestoneDate,
  };
};

export const getSafeDocumentNormalizationPolicy = (
  node: Partial<ProjectNode>,
  targetType: NodeType,
  nodes: Array<Partial<ProjectNode>>,
): NodeTypeConversionPolicyResult => {
  const descendantIds = getDescendantNodeIds(nodes, node.id);
  if (targetType !== 'document') {
    return {
      allowed: false,
      reason: NODE_TYPE_CONVERSION_INVALID_TARGET_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  if (node.type === 'document') {
    return {
      allowed: true,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  if (node.type !== 'task' && node.type !== 'milestone') {
    return {
      allowed: false,
      reason: NODE_TYPE_CONVERSION_INVALID_TARGET_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  if (descendantIds.length > 0) {
    return {
      allowed: false,
      reason: NODE_TYPE_DOCUMENT_NORMALIZATION_DESCENDANTS_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  if (!hasDocumentRecordMarkers(node)) {
    return {
      allowed: false,
      reason: NODE_TYPE_DOCUMENT_NORMALIZATION_MARKER_ERROR,
      targetType,
      descendantCount: descendantIds.length,
      usableMilestoneDate: null,
    };
  }

  return {
    allowed: true,
    targetType,
    descendantCount: descendantIds.length,
    usableMilestoneDate: null,
  };
};

export const buildCanonicalNodeTypeConversionUpdates = (
  node: Partial<ProjectNode>,
  targetType: NodeType,
  nodes: Array<Partial<ProjectNode>>,
  pendingUpdates?: Partial<ProjectNode>,
): { allowed: boolean; reason?: string; updates?: Partial<ProjectNode> } => {
  const policy = getNodeTypeConversionPolicy(node, targetType, nodes, pendingUpdates);
  if (!policy.allowed) {
    return { allowed: false, reason: policy.reason };
  }

  const updates: Partial<ProjectNode> = {
    ...(pendingUpdates || {}),
    type: targetType,
  };

  if (targetType === 'group') {
    updates.dependencies = [];
  }

  if (targetType === 'milestone' && policy.usableMilestoneDate) {
    updates.date = policy.usableMilestoneDate;
    updates.dueDate = policy.usableMilestoneDate;
    updates.endDate = policy.usableMilestoneDate;
  }

  return { allowed: true, updates };
};

export const buildSafeDocumentNormalizationUpdates = (
  node: Partial<ProjectNode>,
  targetType: NodeType,
  nodes: Array<Partial<ProjectNode>>,
  pendingUpdates?: Partial<ProjectNode>,
): { allowed: boolean; reason?: string; updates?: Partial<ProjectNode> } => {
  const policy = getSafeDocumentNormalizationPolicy(node, targetType, nodes);
  if (!policy.allowed) {
    return { allowed: false, reason: policy.reason };
  }

  const meetingRecord = isMeetingRecord({ ...node, ...(pendingUpdates || {}), type: 'document' });
  const nextTags = mergeTags(node.tags, pendingUpdates?.tags);
  if (meetingRecord) {
    nextTags.push('meeting-note');
  }

  return {
    allowed: true,
    updates: {
      ...(pendingUpdates || {}),
      type: 'document',
      dependencies: [],
      tags: Array.from(new Set(nextTags)),
      category: pendingUpdates?.category || node.category || (meetingRecord ? 'meeting-note' : 'document'),
      workstream: pendingUpdates?.workstream || node.workstream || (meetingRecord ? 'meeting' : 'general'),
    },
  };
};

export const getVisibleNodeTypeOptions = (
  node: Partial<ProjectNode>,
  nodes: Array<Partial<ProjectNode>>,
  orderedTypes: Array<Extract<NodeType, 'task' | 'group' | 'milestone'>> = ['task', 'group', 'milestone'],
): Array<Extract<NodeType, 'task' | 'group' | 'milestone'>> =>
  orderedTypes.filter((candidateType) => (
    candidateType === node.type
      ? true
      : getNodeTypeConversionPolicy(node, candidateType, nodes, node).allowed
  ));

export const repairInvalidContainerNodeTypes = (nodes: ProjectNode[]): number => {
  const parentsWithChildren = new Set<Partial<ProjectNode>>();
  nodes.forEach((node) => {
    const parent = findProjectNodeById(nodes, scopedNodeId(node, node.parentId));
    if (parent) {
      parentsWithChildren.add(parent);
    }
  });

  let repairedCount = 0;
  nodes.forEach((node) => {
    if (!node.id) {
      return;
    }

    if (!parentsWithChildren.has(node) || node.type === 'group') {
      return;
    }

    node.type = 'group';
    node.dependencies = [];
    repairedCount += 1;
  });

  return repairedCount;
};
